import { cloneElement } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

/**
 * usePointerReorder 와 함께 쓰는 모션 wrapper.
 *
 * 각 아이템을 감싸면 자동으로:
 *  - data-reorder-index 주입 (children 에)
 *  - 소스가 다른 곳을 호버 중일 때 자기 자신 display:none (gap 합쳐짐 방지)
 *  - 드래그 중 키 변경으로 framer-motion fly-in 회피
 *  - layout='position' 으로 주변 아이템 push/pull 스프링 애니메이션
 *  - self/left/right 위치에 TempButton 자동 배치 (layoutId='active-temp' 슬라이드)
 *
 * 사용법:
 *   const reorder = usePointerReorder({ onDrop });
 *   <Container ref={reorder.containerRef}>
 *     {items.map((item, i) => (
 *       <PointerReorderItem key={item.id} reorder={reorder} index={i}>
 *         <MyButton onPointerDown={(e) => reorder.onPointerDown(e, i)}>
 *           {item.name}
 *         </MyButton>
 *       </PointerReorderItem>
 *     ))}
 *   </Container>
 *
 * @param {object} props
 * @param {object} props.reorder         usePointerReorder() 반환값
 * @param {number} props.index           이 아이템의 인덱스
 * @param {React.ReactElement} props.children  단일 React 엘리먼트 (드래그 핸들 포함)
 * @param {number} [props.gap=10]        컨테이너 gap 값. TempButton 양옆 간격에 사용
 * @param {string} [props.tempButtonColor]  TempButton 배경색. 미지정 시 theme.buttonFill, 없으면 회색
 * @param {number} [props.itemHeight=30] TempButton 높이 (드래그 대상 높이와 맞춤)
 */
export default function PointerReorderItem(props) {
  const { reorder, index, children, gap = 10, tempButtonColor, itemHeight = 30 } = props;
  const { dragSourceIndex, dragOverIndex, dragOverSide, draggedWidth } = reorder;

  const isSource = dragSourceIndex === index;
  const isHidden = isSource && dragOverSide !== 'self';
  const showSelf = isSource && dragOverSide === 'self';
  const showLeft = !isSource && dragSourceIndex !== null && dragOverIndex === index && dragOverSide === 'left';
  const showRight = !isSource && dragSourceIndex !== null && dragOverIndex === index && dragOverSide === 'right';

  // children 에 data-reorder-index 주입 + 소스일 때 display:none //
  const enhancedChild = cloneElement(children, {
    'data-reorder-index': index,
    style: {
      ...(children.props.style ?? {}),
      ...(isSource ? { display: 'none' } : {}),
    },
  });

  return (
    <Wrapper
      key={isHidden ? `h-${index}` : `v-${index}`}
      $isDragging={isHidden}
      layout='position'
      transition={{ type: 'spring', stiffness: 350, damping: 30 }}>
      {
        showSelf &&
        <TempButton width={draggedWidth} side='self' gap={gap} color={tempButtonColor} height={itemHeight} />
      }
      {
        showLeft &&
        <TempButton width={draggedWidth} side='left' gap={gap} color={tempButtonColor} height={itemHeight} />
      }
      {enhancedChild}
      {
        showRight &&
        <TempButton width={draggedWidth} side='right' gap={gap} color={tempButtonColor} height={itemHeight} />
      }
    </Wrapper>
  );
}

function TempButton(props) {
  const { width, side, gap, color, height } = props;

  return (
    <TempButtonMotion
      layoutId='active-temp'
      $color={color}
      $height={height}
      style={{
        width,
        marginLeft: side === 'right' ? gap : 0,
        marginRight: side === 'left' ? gap : 0,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: 'spring', stiffness: 350, damping: 30, opacity: { duration: 0.12 } }}
    />
  );
}

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: row;
  align-items: center;

  ${props => props.$isDragging && `
    display: none;
  `}
`;

const TempButtonMotion = styled(motion.div)`
  flex-shrink: 0;

  height: ${props => props.$height}px;

  border-radius: 5px;
  background-color: ${props => props.$color ?? props.theme?.buttonFill ?? '#d8d8d8'};
`;
